import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils.js';
import { ApiError } from '@/lib/api.js';
import { ListOptions } from '@/components/shared/ListSelect.jsx';
import {
  getDraftReview,
  approveDraft,
  discardDraft,
  fetchSourceBlobUrl,
} from '@/services/quoteIngest.service.js';

// MV-03 §2.2 review screen: source document LEFT, extracted fields RIGHT,
// amber on low-confidence fields, per-field edit. APPROVE writes the quote +
// options (draft → received); no approve, no write. Discard drops the draft
// and keeps the source doc.

const CONF_AMBER_BELOW = 0.8;

// column key → extraction field_confidence key(s)
const CONF_ALIASES = {
  total_premium: ['premium', 'total_premium'],
  liability_csl: ['liability_per_occurrence', 'liability_csl'],
  per_passenger: ['liability_per_passenger', 'per_passenger'],
  medical_per_person: ['medical_payments', 'medical_per_person'],
  named_insured: ['account_name', 'named_insured'],
  opw_text: ['opw', 'opw_text'],
  effective_date: ['effective_date'],
  expiration_date: ['expiration_date'],
  quote_expires_at: ['quote_expires_at'],
  quote_number: ['quote_number'],
  carrier: ['carrier_name', 'carrier'],
  hull_value: ['hull_value'],
  deductible_in_motion: ['deductible_in_motion'],
  deductible_not_in_motion: ['deductible_not_in_motion'],
  coverage_type: ['coverage_type'],
  term_months: ['term_months'],
  estimated_taxes: ['estimated_taxes'],
  pilot_requirements: ['pilot_requirements'],
  endorsements: ['endorsements'],
};

function confFor(confMap, columnKey) {
  if (!confMap || typeof confMap !== 'object') return null;
  for (const k of CONF_ALIASES[columnKey] || [columnKey]) {
    if (typeof confMap[k] === 'number') return confMap[k];
  }
  return null;
}

const HEADER_FIELDS = [
  { key: 'carrier', label: 'Carrier' },
  { key: 'quote_number', label: 'Quote number' },
  { key: 'named_insured', label: 'Named insured' },
  { key: 'effective_date', label: 'Effective date', type: 'date' },
  { key: 'expiration_date', label: 'Term ends', type: 'date' },
  { key: 'quote_expires_at', label: 'Offer expires', type: 'date' },
  { key: 'term_months', label: 'Term (months)', type: 'number' },
  { key: 'pilot_requirements', label: 'Pilot requirements', type: 'textarea' },
  { key: 'opw_text', label: 'Open pilot warranty', type: 'textarea' },
];

const OPTION_FIELDS = [
  { key: 'option_label', label: 'Label' },
  { key: 'coverage_type', label: 'Coverage type', type: 'list' },
  { key: 'total_premium', label: 'Premium (pre-tax)', type: 'number' },
  { key: 'estimated_taxes', label: 'Taxes/fees', type: 'number' },
  { key: 'hull_value', label: 'Hull value', type: 'number' },
  { key: 'deductible_in_motion', label: 'Deductible IM' },
  { key: 'deductible_not_in_motion', label: 'Deductible NIM' },
  { key: 'liability_csl', label: 'Liability (occ)', type: 'number' },
  { key: 'per_passenger', label: 'Per passenger', type: 'number' },
  { key: 'medical_per_person', label: 'Med pay', type: 'number' },
  { key: 'term_months', label: 'Term (months)', type: 'number' },
  { key: 'opw_text', label: 'OPW (this option)', type: 'textarea' },
  { key: 'endorsements', label: 'Endorsements / conditions', type: 'textarea' },
];

const NUMERIC_KEYS = new Set([
  'total_premium', 'estimated_taxes', 'hull_value', 'liability_csl',
  'per_passenger', 'medical_per_person', 'term_months',
]);

function toPatchValue(key, v) {
  if (v === '' || v === undefined) return null;
  if (NUMERIC_KEYS.has(key)) {
    const n = Number(String(v).replace(/[$,]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  return v;
}

function Field({ def, value, conf, onChange }) {
  const amber = conf != null && conf < CONF_AMBER_BELOW;
  const cls = cn(
    'w-full rounded border bg-bg px-2.5 py-1.5 text-[13px] text-text',
    amber ? 'border-warning ring-1 ring-warning/50' : 'border-border'
  );
  return (
    <div>
      <p className="mb-0.5 flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-wide text-text-dim">
        {def.label}
        {amber ? (
          <span className="rounded bg-warning/15 px-1 text-[9px] font-semibold normal-case text-warning">
            check · {Math.round(conf * 100)}%
          </span>
        ) : null}
      </p>
      {def.type === 'textarea' ? (
        <textarea rows={2} className={cls} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      ) : def.type === 'list' ? (
        <select className={cls} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">not stated</option>
          <ListOptions field={def.key} stored={value} />
        </select>
      ) : (
        <input
          type={def.type === 'date' ? 'date' : 'text'}
          className={cls}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

export default function QuoteParseReviewSheet({ quoteId, open, onOpenChange, onResolved }) {
  const [review, setReview] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [header, setHeader] = useState({});
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardReason, setDiscardReason] = useState('');
  const [sourceUrl, setSourceUrl] = useState(null);
  const sourceUrlRef = useRef(null);

  useEffect(() => {
    if (!open || !quoteId) return;
    setReview(null);
    setLoadError('');
    setSourceUrl(null);
    getDraftReview(quoteId)
      .then((r) => {
        setReview(r);
        const q = r.quote || {};
        const h = {};
        for (const f of HEADER_FIELDS) h[f.key] = q[f.key] ?? '';
        setHeader(h);
        setOptions((r.options || []).map((o) => {
          const bag = { id: o.id, _conf: o.raw_data?.field_confidence || null };
          for (const f of OPTION_FIELDS) bag[f.key] = o[f.key] ?? '';
          return bag;
        }));
        if (r.source_doc?.id && r.source_doc?.workdrive_file_id) {
          fetchSourceBlobUrl(r.source_doc.id)
            .then((url) => { sourceUrlRef.current = url; setSourceUrl(url); })
            .catch(() => setSourceUrl(null));
        }
      })
      .catch((err) => setLoadError(err instanceof ApiError ? (err.body?.error || err.code) : err.message));
    return () => {
      if (sourceUrlRef.current) {
        URL.revokeObjectURL(sourceUrlRef.current);
        sourceUrlRef.current = null;
      }
    };
  }, [open, quoteId]);

  const quoteConf = review?.quote?.field_confidence || null;
  const isDraft = review?.quote?.status === 'draft';

  const totalOptions = options.length;
  const flaggedCount = useMemo(() => {
    let n = 0;
    for (const f of HEADER_FIELDS) {
      const c = confFor(quoteConf, f.key);
      if (c != null && c < CONF_AMBER_BELOW) n++;
    }
    for (const o of options) {
      for (const f of OPTION_FIELDS) {
        const c = confFor(o._conf || quoteConf, f.key);
        if (c != null && c < CONF_AMBER_BELOW) n++;
      }
    }
    return n;
  }, [quoteConf, options]);

  async function handleApprove() {
    setBusy(true);
    try {
      const headerPatch = {};
      for (const f of HEADER_FIELDS) headerPatch[f.key] = toPatchValue(f.key, header[f.key]);
      const optionPatches = options.map((o) => {
        const p = { id: o.id };
        for (const f of OPTION_FIELDS) p[f.key] = toPatchValue(f.key, o[f.key]);
        return p;
      });
      const res = await approveDraft(quoteId, { header: headerPatch, options: optionPatches });
      toast.success(
        `Quote approved: ${res.quote?.carrier || ''} · ${totalOptions} option${totalOptions === 1 ? '' : 's'}${res.superseded_quote_id ? ' · prior round folded' : ''}`
      );
      onResolved?.();
      onOpenChange?.(false);
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Approve failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleDiscard() {
    setBusy(true);
    try {
      await discardDraft(quoteId, discardReason.trim() || null);
      toast.success('Parse draft discarded (source document kept)');
      onResolved?.();
      onOpenChange?.(false);
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Discard failed');
    } finally {
      setBusy(false);
      setDiscarding(false);
    }
  }

  const doc = review?.source_doc || null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="mx-auto max-h-[94vh] overflow-y-auto md:max-w-6xl">
        <SheetHeader>
          <SheetTitle>Review quote parse</SheetTitle>
          <SheetDescription>
            Source on the left, extracted terms on the right. Amber fields read below{' '}
            {Math.round(CONF_AMBER_BELOW * 100)}% confidence — check them against the document.
            Nothing enters the books until you approve.
          </SheetDescription>
        </SheetHeader>

        {loadError ? (
          <p className="mt-6 text-sm text-danger">{loadError}</p>
        ) : !review ? (
          <p className="mt-6 text-sm text-text-muted">Loading parse…</p>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {/* ── Source (left) ── */}
            <div className="min-h-[300px]">
              <div className="mb-2 text-[11px] text-text-muted">
                {doc ? (
                  <>
                    <span className="font-medium text-text">{doc.filename || 'Source document'}</span>
                    {doc.from_address ? <> · from {doc.from_name || doc.from_address}</> : null}
                    {doc.subject ? <> · “{doc.subject}”</> : null}
                  </>
                ) : (
                  'No source document on this draft'
                )}
              </div>
              {sourceUrl ? (
                <object data={sourceUrl} type={doc?.mime_type || 'application/pdf'} className="h-[62vh] w-full rounded border border-border">
                  <a href={sourceUrl} target="_blank" rel="noreferrer" className="text-accent underline">Open source document</a>
                </object>
              ) : doc?.body_text ? (
                <pre className="max-h-[62vh] overflow-auto whitespace-pre-wrap rounded border border-border bg-surface p-3 text-[12px] text-text">
                  {doc.body_text}
                </pre>
              ) : (
                <div className="flex h-[30vh] items-center justify-center rounded border border-dashed border-border text-[12.5px] text-text-dim">
                  Source preview unavailable
                </div>
              )}
            </div>

            {/* ── Extracted fields (right) ── */}
            <div className="space-y-4">
              {!isDraft ? (
                <p className="rounded border border-border bg-surface px-3 py-2 text-[12.5px] text-text-muted">
                  This quote is {review.quote?.status} — already past review.
                </p>
              ) : null}
              <div>
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-dim">
                  Quote{review.deal?.account_name ? ` · ${review.deal.account_name}` : ''}
                  {review.quote?.round > 1 ? ` · round ${review.quote.round}` : ''}
                  {flaggedCount ? ` · ${flaggedCount} field${flaggedCount === 1 ? '' : 's'} to check` : ''}
                </p>
                <div className="grid grid-cols-2 gap-2.5">
                  {HEADER_FIELDS.map((f) => (
                    <div key={f.key} className={f.type === 'textarea' ? 'col-span-2' : undefined}>
                      <Field
                        def={f}
                        value={header[f.key]}
                        conf={confFor(quoteConf, f.key)}
                        onChange={(v) => setHeader((h) => ({ ...h, [f.key]: v }))}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {options.map((o, i) => (
                <div key={o.id} className="rounded border border-border bg-surface p-3">
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-dim">
                    Option {i + 1} of {totalOptions}
                  </p>
                  <div className="grid grid-cols-2 gap-2.5">
                    {OPTION_FIELDS.map((f) => (
                      <div key={f.key} className={f.type === 'textarea' ? 'col-span-2' : undefined}>
                        <Field
                          def={f}
                          value={o[f.key]}
                          conf={confFor(o._conf || quoteConf, f.key)}
                          onChange={(v) => setOptions((list) => list.map((x) => (x.id === o.id ? { ...x, [f.key]: v } : x)))}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {isDraft ? (
                <div className="space-y-2 border-t border-border pt-3">
                  <Button onClick={handleApprove} disabled={busy} className="w-full">
                    {busy ? 'Working…' : `Approve · write quote with ${totalOptions} option${totalOptions === 1 ? '' : 's'}`}
                  </Button>
                  {discarding ? (
                    <div className="flex gap-2">
                      <input
                        autoFocus
                        placeholder="Why discard? (optional)"
                        value={discardReason}
                        onChange={(e) => setDiscardReason(e.target.value)}
                        className="w-full rounded border border-border bg-bg px-2.5 py-1.5 text-[13px] text-text"
                      />
                      <Button variant="destructive" onClick={handleDiscard} disabled={busy}>Discard</Button>
                      <Button variant="outline" onClick={() => setDiscarding(false)} disabled={busy}>Keep</Button>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setDiscarding(true)} disabled={busy} className="w-full">
                      Discard parse draft
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
